/** @format */
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import React from "react";
import { useForm } from "react-hook-form";

import { useLogin, useUser } from "@/api/userAPI";
import { type LoginFormData, loginSchema } from "@/lib/schemas";
import { setFormErrorsFromApi } from "@/lib/utils/formErrorHandling";
import { trackEvent } from "@/utils/analytics";
import LoadingCircle from "@/utils/LoadingCircle";

const Login = ({
  setModalComponent,
  setModalOpen,
}: {
  setModalComponent: (component: string | null) => void;
  setModalOpen: (open: boolean) => void;
}) => {
  const { data: user, refetch } = useUser({ enabled: false });
  const navigate = useNavigate();
  const login = useLogin();

  // Redirect if user is already logged in
  React.useEffect(() => {
    if (user?.id) {
      void navigate({ to: "/", replace: true });
    }
  }, [user?.id, navigate]);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: true,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data.email, data.password, data.rememberMe);
      await refetch();
      trackEvent("login_completed", { method: "password" });
      setModalOpen(false);
      setModalComponent(null);
    } catch (error: unknown) {
      trackEvent("login_failed", { error_type: "invalid_credentials" });
      setFormErrorsFromApi(error, setError, {
        email: "email",
        password: "password",
      });
      console.error(error);
    }
  };

  if (user?.id) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="self-center content-center bg-slate-900 rounded-xl p-4 w-full max-w-2xl mt-2 text-white">
      <h1 className="text-xl mb-2">Login</h1>
      <form onSubmit={e => void handleSubmit(onSubmit)(e)} className="space-y-6">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-200">
            E-Mail
          </label>
          <div className="mt-1">
            <input
              type="email"
              id="email"
              autoComplete="email"
              required
              {...register("email")}
              className="bg-black appearance-none block w-full px-3 py-2 border border-slate-500 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 sm:text-sm"
            />
          </div>
          {errors.email && <div className="text-red-600">{errors.email.message}</div>}
        </div>
        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-slate-200"
          >
            Password
          </label>
          <div className="mt-1">
            <input
              type="password"
              id="password"
              autoComplete="current-password"
              required
              {...register("password")}
              className="appearance-none block w-full px-3 py-2 border bg-black border-slate-500 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-cPurple-500 focus:border-cPurple-500 sm:text-sm"
            />
          </div>
          {errors.password && (
            <div className="text-red-600">{errors.password.message}</div>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="rememberMe"
              type="checkbox"
              {...register("rememberMe")}
              className="h-4 w-4 text-cPurple-600 focus:ring-cPurple-500 border-slate-500 rounded"
            />
            <label htmlFor="rememberMe" className="ml-2 block text-sm text-slate-400">
              Remember me
            </label>
          </div>
        </div>
        <div>
          <button
            aria-label="Login"
            type="submit"
            disabled={isSubmitting || Object.keys(errors).length > 0}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-500 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <LoadingCircle className="animate-spin h-5 w-5 mr-1 text-white inline-block" />
                Loading
              </>
            ) : (
              "Login"
            )}
          </button>
        </div>
        <div className="text-sm text-center text-slate-400 gap-1 grid grid-flow-col grid-cols-max justify-center">
          {/** //ToDo: Make this Prettier**/}
          <button
            aria-label="Reset Password"
            type="button"
            onClick={() => {
              setModalComponent("resetPassword");
            }}
            className="font-medium text-cPurple-600 hover:text-cPurple-500"
          >
            Forgot your password?
          </button>
          |
          <button
            aria-label="Register"
            type="button"
            onClick={() => {
              setModalComponent("register");
            }}
            className="font-medium text-cPurple-600 hover:text-cPurple-500"
          >
            Sign Up
          </button>
        </div>
      </form>
    </div>
  );
};

export default Login;
